<template lang="pug">
  .width-100
    search-bar.m-bottom-2(@submitSearch='filterUsers' @resetFilters='resetFilters')
    paginated-table(
      :pageSize='10'
      :getPage='getPage'
      :totalCount='totalCount'
      ref='paginatedTable'
      controlSize
      @pageChanged='deselectAll'
      v-slot='slotProps'
    )
      .table.column
        delete-members-modal(v-if='deleteMembersModal' @deletedUsers='reloadPage' :pageNumber='slotProps.pageNumber')
        .table-header.row.middle
          .m-right-1.m-left-1.row.middle.center(v-if='!isTeacher')
            checkbox(:updateCheck='toggleSelectAll' white :filled='false' :checked='areAllSelected')
          .item-5.row.middle.name(:class='{"m-left-2": isTeacher}' @click='toggleNameOrder')
            h2.text-normal.white.bold {{ $t('team.list.name') }}
            simple-svg.order-icon.m-left-1.white-icon(:filepath='arrowIcon' width='16px' height='16px' :class='{up: nameOrder}')
          h2.text-normal.white.bold.item-2 {{ $t('team.list.role') }}
          h2.text-normal.white.bold.item-2(v-if='isKreaAdmin') {{ $t('team.list.kreaRole') }}
          h2.text-normal.white.bold.item-2.m-right-2 {{ $t('team.list.level') }}
          h2.text-normal.white.bold.item-2 {{ $t('team.list.status') }}
          h2.text-normal.white.bold.item-2.m-right-4(v-if='!isTeacher') {{ $t('team.list.actions') }}
        .width-100
          .selected-users.row.middle(v-if='selectedUsers.length')
            checkbox.m-right-05(:updateCheck='deselectAll' :checked='true')
            h2.text-normal.m-right-4 {{ $t(`team.list.selectedUsers.amount`, { selectedUsers: selectedUsers.length }) }}
            button.row.middle.m-right-2(@click='deleteMembers')
              simple-svg.m-right-1.blue-icon(:filepath='removeIcon' width='18px' height='18px')
              h2.text-normal.blue {{ $t('team.list.selectedUsers.delete') }}
            button.row.middle(v-if='hasPendings' @click='resend')
              simple-svg.m-right-1.blue-icon(:filepath='sendIcon' width='20px' height='20px')
              h2.text-normal.blue {{ $t('team.list.selectedUsers.sendInvitations') }}
          .users.relative
            loader.absolute.loader(v-if='loading' size='inner')
            user-row(
              v-else
              :pageNumber='slotProps.pageNumber'
              v-for='user in users'
              :user='user'
              :key='user.userId'
              @editedUser='reloadPage'
              @resendInvitation='reloadPage'
            )
</template>

<script>
import SearchBar from '@/components/Team/SearchBar'
import PaginatedTable from '@/components/PaginatedTable'
import UserRow from './UserRow'
import checkboxDefaultIcon from '@/assets/ic_checkbox_default_transparent.svg'
import checkboxActiveTransparentIcon from '@/assets/ic_checkbox_active_transparent.svg'
import checkboxActiveIcon from '@/assets/ic_checkbox_active.svg'
import removeIcon from '@/assets/ic_trash.svg'
import sendIcon from '@/assets/ic_send.svg'
import Loader from '@/components/Loader'
import { mapActions, mapState } from 'vuex'
import { firstElement, teamMembersStatus, userPrimaryRoles } from '../../utils/constants'
import userRole from '../../mixins/userRole'
import DeleteMembersModal from '@/components/Team/DeleteMembersModal'
import arrowIcon from '@/assets/ic_chevron-down.svg'
import kreaRoles from '../../mixins/kreaRoles'
import Checkbox from '../Inputs/Checkbox'

export default {
  mixins: [userRole, kreaRoles],
  components: { Checkbox, Loader, SearchBar, PaginatedTable, UserRow, DeleteMembersModal },
  data() {
    return {
      sendIcon,
      removeIcon,
      checkboxActiveIcon,
      checkboxActiveTransparentIcon,
      checkboxDefaultIcon,
      selectAll: false,
      loading: true,
      filters: {},
      nameOrder: false,
      arrowIcon
    }
  },
  async created() {
    await this.hydrateEducationalLevels()
    await this.getPage()
  },
  computed: {
    ...mapState({
      users: state => state.team.allUsers.users,
      totalCount: state => state.team.allUsers.totalCount,
      selectedUsers: state => state.team.selectedUsers,
      userInfo: state => state.userInfo.userInfo,
      currentSchool: state => state.profile.userProfile.schoolGroupVO.currentSchool,
      deleteMembersModal: state => state.behavior.deleteMembersModal.show
    }),
    areAllSelected() {
      if (!this.users.length) return false
      return this.isManager
        ? this.users.filter(user => user.role === userPrimaryRoles.teacher).length === this.selectedUsers.length
        : this.users.length === this.selectedUsers.length
    },
    hasPendings() {
      return this.selectedUsers.some(user => user.invitationStatus.toLowerCase() !== teamMembersStatus.confirmed)
    },
    schoolId() {
      return this.$route.params.schoolId
    }
  },
  beforeDestroy() {
    this.clearTeamUsers()
  },
  methods: {
    ...mapActions([
      'deselectAllTeamUsers',
      'selectAllTeamUsers',
      'hydrateEducationalLevels',
      'resendInvitations',
      'showElement',
      'hideElement',
      'getUsersInSchool',
      'clearTeamUsers'
    ]),
    toggleSelectAll() {
      if (!this.areAllSelected) this.selectAllTeamUsers(this.userInfo.primaryRoles[firstElement].name)
      else this.deselectAllTeamUsers()
    },
    deselectAll() {
      this.deselectAllTeamUsers()
    },
    async getPage(pageNumber, pageSize) {
      this.loading = true
      const success = await this.getUsersInSchool({
        id: this.schoolId,
        filters: {
          pageNumber,
          pageSize,
          ...this.filters
        }
      })
      this.loading = false
      return success
    },
    filterUsers(newFilters) {
      this.filters = newFilters
      this.getPage()
    },
    resetFilters() {
      this.filters = {}
      this.getPage()
    },
    toggleNameOrder() {
      this.nameOrder = !this.nameOrder
      this.filters = {
        ...this.filters,
        orderAsc: this.nameOrder
      }
      this.getPage()
    },
    async resend() {
      this.showElement({ element: 'fullPageLoader' })
      await this.resendInvitations(this.schoolId)
      this.hideElement({ element: 'fullPageLoader' })
    },
    deleteMembers() {
      this.showElement({ element: 'deleteMembersModal' })
    },
    reloadPage(pageNumber) {
      this.getPage(pageNumber)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.table-header{
  background-color: $blue;
  height: 50px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  z-index: 1;
}

.users{
  min-height: 520px;
  .loader{
    left: 50%;
    transform: translateX(-50%);
  }
  &>:nth-child(even){
    background-color: $light-gray;
  }
  &>:nth-child(odd){
    background-color: $white;
  }
}

.selected-users{
  z-index: 1;
  position: relative;
  height: 60px;
  padding: 0 16px;
  background-color: $lighter-blue;
}

.table-header, .selected-users{
  box-shadow: 0px 5px 10px -1px $shadow-gray;
}

.name{
  cursor: pointer;
}

.order-icon{
  &.up{
    transform: rotate(180deg);
  }
}

</style>
